import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { z } from 'zod';

import { useCategories } from '@/api/category/use-categories.ts';
import { useApplyPattern } from '@/api/pattern/use-apply-pattern.ts';
import { useCreatePattern } from '@/api/pattern/use-create-pattern.ts';
import { useSuggestParties } from '@/api/pattern/use-suggest-parties.ts';
import type { CreatePatternDto } from '@/api/types';
import FormInput from '@/components/form/form-input/form-input.tsx';
import FormSelect from '@/components/form/form-select/form-select.tsx';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx';
import { formatPrice, parsePrice } from '@/helpers/formatters.ts';

const addPatternSchema = z.object({
  party: z.string().min(1, 'Party is required'),
  amount: z.string().optional(),
  description: z.string().optional(),
  categoryId: z.string().min(1, 'Category is required'),
});

type AddPatternFormValues = {
  party: string;
  amount?: string;
  description?: string;
  categoryId: string;
};

type AddPatternModalProps = {
  onClose: () => void;
  initialParty?: string;
  initialAmount?: number;
};

const AddPatternModal = ({ onClose, initialParty, initialAmount }: AddPatternModalProps) => {
  const { projectId } = useParams();

  const { data: categories } = useCategories(projectId);
  const { mutateAsync: createPattern, isPending: createPending } = useCreatePattern(projectId!);
  const { mutateAsync: applyPattern, isPending: applyPending } = useApplyPattern(projectId!);

  const form = useForm<AddPatternFormValues>({
    resolver: zodResolver(addPatternSchema),
    defaultValues: {
      party: initialParty || '',
      amount: initialAmount != null ? String(initialAmount / 100) : '',
    },
  });

  const { data: partySuggestions } = useSuggestParties(projectId, form.watch('party'));

  const buildPayload = (data: AddPatternFormValues): CreatePatternDto => ({
    party: data.party,
    amount: data.amount ? parsePrice(Number(data.amount)) : undefined,
    description: data.description || undefined,
    categoryId: data.categoryId,
  });

  const onSave = (data: AddPatternFormValues) => {
    createPattern(buildPayload(data))
      .then(() => onClose())
      .catch(() => undefined);
  };

  const onSaveAndApply = (data: AddPatternFormValues) => {
    createPattern(buildPayload(data))
      .then((pattern) => applyPattern(pattern.uuid))
      .then(() => onClose())
      .catch(() => undefined);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const isPending = createPending || applyPending;

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <FormProvider {...form}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Pattern</DialogTitle>
            <DialogDescription>
              Transfers matching this party (and amount, if set) will be auto-categorized with this
              description and category.
            </DialogDescription>
          </DialogHeader>
          <FormInput name="party" label="Party" list="party-suggestions" />
          <datalist id="party-suggestions">
            {partySuggestions?.map((party) => (
              <option key={party} value={party} />
            ))}
          </datalist>
          <FormInput
            name="amount"
            label="Amount (optional)"
            type="number"
            step="0.01"
            placeholder="Leave empty to match any amount"
          />
          {initialAmount != null && (
            <p className="text-xs text-muted-foreground -mt-2">
              Prefilled with this transfer's amount ({formatPrice(initialAmount)}) — clear it to
              match any amount for this party instead.
            </p>
          )}
          <FormInput
            name="description"
            label="Description"
            placeholder="e.g. Netflix, Subscription"
          />
          <FormSelect
            name="categoryId"
            label="Category"
            options={[
              { value: '', label: 'Select category' },
              ...(categories?.map((category) => ({
                value: category.uuid,
                label: category.name,
              })) || []),
            ]}
          />
          <DialogFooter>
            <Button onClick={() => onClose()} variant="outline">
              Cancel
            </Button>
            <Button disabled={isPending} onClick={form.handleSubmit(onSave)} type="submit">
              Save
            </Button>
            <Button disabled={isPending} onClick={form.handleSubmit(onSaveAndApply)} type="submit">
              Save &amp; Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
};

export default AddPatternModal;
